import React, { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Minus, Plus, UtensilsCrossed, Loader, CheckCircle2 } from "lucide-react";
import Header from "./components/Header";
import BackButton from "./components/BackButton";
import PrimaryButton from "./components/PrimaryButton";
import CategorySlider from "./components/CategorySlider";
import useNav from "./hooks/useNav";
import { useRestaurant } from "./context/RestaurantContext.jsx";
import { useAuthFetch } from "./hooks/useAuthFetch";
import { toast } from "react-toastify";
import appConfig from "./config";

const PreOrderDishCard = ({ dish, quantity, onAdd, onIncrease, onDecrease }) => {
    const [imgError, setImgError] = useState(false);

    return (
        <div className="relative bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
            <div className="bg-gray-100 w-full aspect-[4/3] overflow-hidden relative">
                {!imgError && dish.image ? (
                    <img
                        src={dish.image}
                        alt={dish.title}
                        className="w-full h-full object-cover"
                        onError={() => setImgError(true)}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <UtensilsCrossed size={36} />
                    </div>
                )}
                {quantity > 0 && (
                    <div className="absolute top-2 left-2 bg-brandBlue text-white text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm">
                        {quantity} шт.
                    </div>
                )}
            </div>
            <div className="p-3 flex flex-col flex-1">
                <p className="text-sm font-semibold text-gray-900 leading-tight mb-1 line-clamp-2">{dish.title}</p>
                <p className="text-xs text-gray-400 mb-3 flex-1 line-clamp-2">{dish.description}</p>
                <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-900">{dish.price} ₽</span>
                    {quantity === 0 ? (
                        <button
                            onClick={onAdd}
                            className="w-8 h-8 flex items-center justify-center rounded-xl bg-brandBlue text-white hover:bg-brandBlue/90 transition-colors"
                        >
                            <Plus size={14} strokeWidth={2.5} />
                        </button>
                    ) : (
                        <div className="flex items-center gap-2 bg-gray-50 rounded-xl border border-gray-200 p-0.5">
                            <button
                                onClick={onDecrease}
                                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:bg-white transition-all"
                            >
                                <Minus size={12} strokeWidth={2.5} />
                            </button>
                            <span className="text-sm font-bold text-gray-900 min-w-[18px] text-center">{quantity}</span>
                            <button
                                onClick={onIncrease}
                                className="w-7 h-7 flex items-center justify-center rounded-lg bg-brandBlue text-white hover:bg-brandBlue/90 transition-colors"
                            >
                                <Plus size={12} strokeWidth={2.5} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ReservationPreOrderPage = () => {
    const navigate = useNav();
    const { state } = useLocation();
    const reservationId = state?.reservationId;
    const date = state?.date;
    const time = state?.time;
    const existingDishes = state?.existingDishes;

    const { restaurant, menu, categories } = useRestaurant();
    const { authFetch } = useAuthFetch();

    const minutesUntil = date && time
        ? (new Date(`${date}T${time}`).getTime() - Date.now()) / 60000
        : 9999;
    const canPreorder = minutesUntil > 30;

    // Если пришли с existingDishes — предзаполняем quantities
    const [quantities, setQuantities] = useState(() => {
        if (!existingDishes?.length) return {};
        return existingDishes.reduce((acc, d) => {
            if (d.id) acc[d.id] = d.quantity || 1;
            return acc;
        }, {});
    });

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const activeCategory = selectedCategory || categories[0]?.title;

    const filteredDishes = useMemo(() => {
        if (!activeCategory || !menu.length) return [];
        return menu.filter(d => d.category?.toLowerCase() === activeCategory.toLowerCase());
    }, [menu, activeCategory]);

    const totalItems = Object.values(quantities).reduce((s, q) => s + q, 0);
    const totalPrice = Object.entries(quantities).reduce((s, [id, q]) => {
        const dish = menu.find(d => String(d.id) === String(id));
        return s + (dish?.price || 0) * q;
    }, 0);

    const haptic = (type = 'light') => window.Telegram?.WebApp?.HapticFeedback?.impactOccurred(type);

    const handleAdd = (dish) => {
        haptic();
        setQuantities(prev => ({ ...prev, [dish.id]: (prev[dish.id] || 0) + 1 }));
    };

    const handleIncrease = (dish) => {
        haptic();
        setQuantities(prev => ({ ...prev, [dish.id]: (prev[dish.id] || 0) + 1 }));
    };

    const handleDecrease = (dish) => {
        haptic();
        setQuantities(prev => {
            const next = { ...prev };
            if ((next[dish.id] || 0) <= 1) {
                delete next[dish.id];
            } else {
                next[dish.id] = next[dish.id] - 1;
            }
            return next;
        });
    };

    const handleConfirm = async () => {
        if (totalItems === 0) {
            toast.error("Добавьте хотя бы одно блюдо");
            return;
        }

        const dishes = Object.entries(quantities)
            .filter(([, q]) => q > 0)
            .map(([id, q]) => {
                const dish = menu.find(d => String(d.id) === String(id));
                return { id: dish.id, title: dish.title, price: dish.price, quantity: q };
            });

        setIsSubmitting(true);
        try {
            const data = await authFetch(`${appConfig.endpoint}/reservations/${reservationId}/preorder`, {
                method: "POST",
                body: JSON.stringify({ dishes }),
            });

            if (data.success) {
                window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
                setDone(true);
            } else {
                toast.error(data.message || "Ошибка при сохранении предзаказа");
            }
        } catch {
            toast.error("Произошла ошибка. Попробуйте снова.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!canPreorder) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Header restaurantName={restaurant?.name} />
                <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mb-5">
                        <UtensilsCrossed size={28} className="text-orange-400" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900 mb-2">Предзаказ недоступен</h2>
                    <p className="text-sm text-gray-500 mb-8">
                        Изменить предзаказ можно не позднее чем за 30 минут до начала брони
                    </p>
                    <button
                        onClick={() => navigate(-1)}
                        className="w-full max-w-sm bg-white border border-gray-200 text-gray-700 rounded-2xl px-5 py-4 font-medium text-sm"
                    >
                        Назад
                    </button>
                </div>
            </div>
        );
    }

    if (done) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <Header restaurantName={restaurant?.name} />
                <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
                    <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-5">
                        <CheckCircle2 size={40} className="text-green-500" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Предзаказ оформлен!</h2>
                    <p className="text-sm text-gray-500 mb-1">Блюда будут готовы к вашему приходу</p>
                    {date && time && (
                        <p className="text-sm text-brandBlue font-medium mb-8">
                            {new Date(date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })} в {time}
                        </p>
                    )}
                    <button
                        onClick={() => navigate('/')}
                        className="w-full max-w-sm bg-brandBlue text-white rounded-2xl px-5 py-4 font-semibold text-sm"
                    >
                        На главную
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header restaurantName={restaurant?.name} />

            <div className="max-w-screen-md mx-auto pt-5 pb-36 page-enter">
                <div className="flex items-center gap-3 mb-4 px-4">
                    <BackButton onClick={() => navigate(-1)} disabled={isSubmitting} />
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">Предзаказ блюд</h1>
                        {date && time && (
                            <p className="text-xs text-gray-400 mt-0.5">
                                к брони на {new Date(date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })} в {time}
                            </p>
                        )}
                    </div>
                </div>

                <div className="bg-brandBlue/8 border border-brandBlue/20 rounded-2xl mx-4 px-4 py-3 mb-4 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-brandBlue/15 flex items-center justify-center shrink-0">
                        <UtensilsCrossed size={15} className="text-brandBlue" />
                    </div>
                    <p className="text-xs text-gray-700 leading-snug">
                        Блюда будут переданы на кухню заранее — вас встретят горячим заказом.
                        Оплата при посещении.
                    </p>
                </div>

                {categories.length > 0 && (
                    <CategorySlider
                        categories={categories}
                        selected={activeCategory}
                        onSelect={(cat) => setSelectedCategory(cat.title)}
                    />
                )}

                <div className="grid grid-cols-2 gap-3 px-4 mt-3">
                    {filteredDishes.map(dish => (
                        <PreOrderDishCard
                            key={dish.id}
                            dish={dish}
                            quantity={quantities[dish.id] || 0}
                            onAdd={() => handleAdd(dish)}
                            onIncrease={() => handleIncrease(dish)}
                            onDecrease={() => handleDecrease(dish)}
                        />
                    ))}
                </div>
            </div>

            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-4 shadow-lg z-10">
                <div className="max-w-screen-md mx-auto space-y-2">
                    {totalItems > 0 && (
                        <div className="flex items-center justify-between px-1 mb-1">
                            <span className="text-sm text-gray-500">{totalItems} {totalItems === 1 ? 'блюдо' : totalItems < 5 ? 'блюда' : 'блюд'}</span>
                            <span className="text-sm font-bold text-gray-900">{totalPrice.toLocaleString('ru-RU')} ₽</span>
                        </div>
                    )}
                    <PrimaryButton
                        onClick={handleConfirm}
                        disabled={isSubmitting || totalItems === 0}
                        className="w-full flex items-center justify-center gap-2 py-3.5"
                    >
                        {isSubmitting ? <Loader className="animate-spin" size={18} /> : "Подтвердить предзаказ"}
                    </PrimaryButton>
                    <button
                        onClick={() => navigate('/')}
                        disabled={isSubmitting}
                        className="w-full text-sm text-gray-500 py-2"
                    >
                        Пропустить
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReservationPreOrderPage;
